/**
 * ============================================================================
 * ⭐⭐ D3 — THE MATCH PICKER (Decision 238, 2026-09-28)
 * ============================================================================
 *
 * > Pete: *"Match on each unmatched order"* — the order's Match button opens
 * > this list of the day's appointments; choosing one attaches the order.
 *
 * ⛔ IT ATTACHES NOTHING ITSELF. `onChoose` hands the appointment back to the
 * page, which runs the SAME path as a drag (A5): ask the server where the order
 * already is, then attach at once or show the replace popup. One gesture, one
 * set of rules, whichever way the scheduler starts it.
 *
 * ⚠️ THE LIST IS THE DAY'S APPOINTMENTS, UNFILTERED BY THE BOARD — the same
 * source the arrival attach picker uses, so a load hidden by a status filter can
 * still be chosen. Blocks and cancelled loads are left out for the reasons the
 * drop refuses them (`wmsOrderRefusal`).
 *
 * ⚠️ NO CLIENT GUESSING. BusinessUnit → client has no populated map
 * (`dbo.WmsCustomerMap`, zero rows), so the list is not pre-filtered to "this
 * client's" loads; it is sorted by time and the box filters it by what you type.
 *
 * ---- ⭐⭐ THE SIDE-BY-SIDE (Pete, 2026-09-28; Decision 241) -----------------
 *
 * > *"This screen should show you details of the appintment on one side (6
 * > criteria) and details of the WMS order on the other side (6 criteria). Along
 * > with the obvious client, carrier. So the scheduler can say, yes this order
 * > goes with this appointment."*
 *
 * ⭐ LIST LEFT, COMPARISON RIGHT, THEN ATTACH. Clicking a load SELECTS it; the
 * comparison (`compareApptToOrder`) shows its Client, Carrier and six rows beside
 * the order's, ticked where they agree and amber where they differ. Nothing
 * attaches until "Attach to A…" is pressed.
 *   ** A PICKER THAT ATTACHES ON THE FIRST CLICK ASKS THE SCHEDULER TO BE SURE
 *      BEFORE SHOWING HIM WHY HE SHOULD BE. **
 *
 * ---- ⭐ FIRST LOAD PRE-SELECTED; HOVER PREVIEWS, CLICK KEEPS (Pete, 2026-09-28) ---
 *
 * > *"Can you make the first one auto highlight and show the right hand screen and
 * > then make the same hover functionality where if you mouse over the appointment,
 * > it shows the right-hand side and if you single click, it stays"*
 *
 * ⭐ The first load in the list is selected on open (and again whenever the filter
 * removes the selected one), so the right side is never empty. Hovering a load
 * PREVIEWS it on the right; leaving the list returns to the selection; a click makes
 * it the selection — the rail's own "hover scopes, click latches" rule.
 * ⛔ ATTACH ALWAYS NAMES THE **SELECTION**, never the hover: a button whose target
 * changed because the cursor crossed a row on the way to it would attach the wrong
 * load. While a preview differs from the selection the right side says so.
 *   ** A PREVIEW MAY FOLLOW THE MOUSE. A COMMIT MAY NOT. **
 * ⚠️ Each list row carries a "✓ N" chip — how many of the eight rows agree — so a
 * likely load can be spotted without clicking every one. The ORDER stays by time
 * (a sort by score would move the list under the cursor as it is read).
 *
 * ---- ⭐⭐ F107 (2026-09-29, Decision 284): THE SAME SCREEN, FROM THE "NO WMS" CHIP ---
 *
 * > Pete: *"open our match screen with the information from the warning instead of
 * > the match information for that day? then they can see all non-matched
 * > appointments in a list and go down the list. One additional feature - lets add
 * > the wms order search below Agrees Differs line (for this screen in general) that
 * > you can search orders and the populate list right below and you can match one of
 * > those if necessary. My guess is they are gonna search on the client/carrier and
 * > want a sequential list to figure out which one should match"*
 *
 * ⭐ TWO MODES, ONE SCREEN.
 *   · `order`  — as before: one order, the day's loads on the left.
 *   · `noWms`  — the No WMS list on the left (past visits with no order), no order
 *                chosen yet; the scheduler finds one with the search.
 * ⭐ THE ORDER SEARCH (both modes) sits under the agrees / differs legend. It asks
 * the Orders panel's own search (`getPanelOrders`, every date once 3 characters are
 * typed, every status) — so client, customer, PO, reference, container, BOL and PRO
 * all work — and lists the results IN DATE ORDER. Clicking a result makes it the
 * order being compared; Attach attaches THAT order.
 *   ⛔ ATTACH NAMES THE CHOSEN LOAD AND THE CHOSEN ORDER — never a hovered one.
 * ⭐ BESIDE THE SEARCH, THE ORDERS PANEL'S TWO VIEWS (Pete, same morning: *"Can we
 * have those buttons replicated next to the search bar … That way they can search,
 * they can see unmatched scheduled for today and the other set to make a good
 * decision, in one spot"*). **Not matched** lists the orders the WMS dates for the
 * SELECTED LOAD'S DAY that are on no appointment; **All scheduled** lists every order
 * dated that day. Every status (a past load's order is usually closed). Same icons
 * as the panel. The results replace the search's, sorted the same way.
 * ⭐ In `noWms` mode the screen stays open after an attach; the page refreshes the
 * list, the matched load leaves it, and the next one is selected.
 *
 * ---- ⭐⭐ F127 (2026-09-29, Decision 305): POSSIBLE MATCHES, FOLLOWING THE HOVER ---
 *
 * > Pete: *"where did the suggestion go on top - possible matches?"* → *"Load's day,
 * > best first"* · *"Should be the same as before. When you hover the left ones, the
 * > right shows the matches"*.
 *
 * ⭐ In `noWms` mode the right side now fills itself for the load under the cursor
 * (else the selected one): its day's orders that are on NO appointment, kept when they
 * share a PO, reference, container or BOL/PRO with the load, or have its client AND
 * direction — strongest first, up to five (`rankPossibleMatches`). The best one is
 * compared in the table straight away, exactly as hovering worked in `order` mode.
 * ⛔ A SUGGESTION IS NEVER ATTACHED BY ITSELF: Attach needs an order CLICKED (from
 * the possible matches or the search). The table says "best possible match — click it
 * to choose it" until then.
 *   ** A PREVIEW MAY FOLLOW THE MOUSE. A COMMIT MAY NOT. **
 * ⭐ One read per DAY, not per hover: the day's unmatched orders are fetched once and
 * kept while the screen is open, so sweeping the list costs nothing after the first
 * pass over each date.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, AlertTriangle, Minus, Search, Unlink, CalendarDays } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getPanelOrders, type OrdersPanelRowDto } from '@/api/client';
import { appointmentTimeLabel, hhmm } from '@/lib/utils';
import { wmsOrderKeyOf, type WmsOrderDragKey } from '@/lib/dnd';
import {
    compareApptToOrder, matchCount, apptLocalDate, type CompareAppt, type CompareRow, type Verdict,
} from '@/lib/matchCompare';

/**
 * ⭐ WHAT THE SCREEN NEEDS OF AN APPOINTMENT. `AppointmentDto` satisfies it (the
 * day list); so does the No WMS list's `NoWmsRowDto`, which is why that list can be
 * handed over as-is.
 */
export type MatchableAppointment = CompareAppt & {
    appointmentId: number;
    appointmentNumber: string;
    statusCode: string;
    dockDoorCode: string | null;
    schedulingMode?: string | null;
};

export interface WmsOrderMatchDialogProps {
    open: boolean;
    /** ⭐ `order` (default): one order, the day's loads. `noWms`: the No WMS list, order found by search. */
    mode?: 'order' | 'noWms';
    /** The order the screen opened for (`order` mode). Blank in `noWms` mode. */
    orderNumber: string;
    /** Its drag key — what Attach attaches when no other order is picked from the search. */
    orderKey?: WmsOrderDragKey | null;
    /**
     * ⭐ The order's own row, for the right-hand side. ⚠️ Optional: without it the
     * comparison says so and the list still works, rather than refusing to open.
     */
    order?: OrdersPanelRowDto | null;
    /** `order` mode: the day's appointments. `noWms` mode: the No WMS list. */
    appointments: readonly MatchableAppointment[];
    /** `noWms` mode: how many there are in all (the list may be capped). */
    total?: number;
    /** ⭐ For the order search. Without it the search box is not drawn. */
    facilityCode?: string | null;
    getToken?: () => Promise<string>;
    onChoose: (appt: MatchableAppointment, order: WmsOrderDragKey, row: OrdersPanelRowDto | null) => void;
    onCancel: () => void;
}

/**
 * ⛔ The appointments an order can be matched to: never a block (a door outage, not
 * a load) and never a cancelled one. Departed loads stay — paperwork is often
 * matched after the truck has gone. Sorted by start time.
 */
export function matchCandidates<T extends { direction: string; statusCode: string; scheduledStartUtc: string; appointmentNumber: string }>(
    appointments: readonly T[],
): T[] {
    return appointments
        .filter(a => a.direction !== 'BLOCK' && a.statusCode !== 'CANCELLED')
        .slice()
        .sort((a, b) => a.scheduledStartUtc.localeCompare(b.scheduledStartUtc)
            || a.appointmentNumber.localeCompare(b.appointmentNumber));
}

function haystack(a: MatchableAppointment): string {
    return [a.appointmentNumber, a.dockDoorCode, a.clientCode, a.clientName, a.carrierCode, a.carrierName,
        a.poNumber, a.referenceNumber, a.documentNumber, a.containerNumber, a.bolNumber]
        .filter(Boolean).join(' ').toLowerCase();
}

/**
 * ⭐ "A SEQUENTIAL LIST" (Pete) — search results in date order, oldest first, then
 * by order number, so a run of one client's orders reads like a ledger. Exported for
 * the guard.
 */
export function sortOrderResults(rows: readonly OrdersPanelRowDto[]): OrdersPanelRowDto[] {
    return rows.slice().sort((a, b) =>
        (a.effectiveDate ?? '9999').localeCompare(b.effectiveDate ?? '9999')
        || a.orderNumber.localeCompare(b.orderNumber));
}

/** The identifiers: one of these agreeing is strong evidence on its own. */
const STRONG_KEYS = new Set<CompareRow['key']>(['po', 'reference', 'container', 'bolPro']);
export const POSSIBLE_MATCH_MAX = 5;

export interface PossibleMatch { row: OrdersPanelRowDto; strong: number; agree: number; }

/**
 * ⭐ F127: the orders worth showing for a load, best first. Kept when an identifier
 * agrees (PO, reference, container, BOL/PRO), or when the client AND the direction do.
 * Sorted by identifiers agreeing, then rows agreeing, then order number. Exported for
 * the guard, which runs it.
 */
export function rankPossibleMatches(
    appt: CompareAppt, orders: readonly OrdersPanelRowDto[], max = POSSIBLE_MATCH_MAX,
): PossibleMatch[] {
    const out: PossibleMatch[] = [];
    for (const row of orders) {
        const cmp = compareApptToOrder(appt, row);
        const strong = cmp.filter(r => STRONG_KEYS.has(r.key) && r.verdict === 'match').length;
        const client = cmp.find(r => r.key === 'client')?.verdict === 'match';
        const direction = cmp.find(r => r.key === 'direction')?.verdict === 'match';
        if (strong > 0 || (client && direction)) out.push({ row, strong, agree: matchCount(cmp) });
    }
    return out
        .sort((a, b) => b.strong - a.strong || b.agree - a.agree || a.row.orderNumber.localeCompare(b.row.orderNumber))
        .slice(0, max);
}

/** The orders-panel search needs a day; any day will do once the search spans every date. */
function dayOf(iso: string | null | undefined): string {
    const d = iso ? apptLocalDate(iso) : null;
    if (d) return d;
    const n = new Date();
    const p = (x: number) => String(x).padStart(2, '0');
    return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
}

function VerdictMark({ v }: { v: Verdict }) {
    if (v === 'match') return <Check className="h-3.5 w-3.5 text-emerald-600" aria-label="Agrees" />;
    if (v === 'differ') return <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-label="Differs" />;
    return <Minus className="h-3.5 w-3.5 text-muted-foreground/60" aria-label="One side is blank" />;
}

const cellTone = (v: Verdict) =>
    v === 'match' ? 'bg-emerald-50 text-emerald-900'
        : v === 'differ' ? 'bg-amber-50 text-amber-900'
            : 'text-muted-foreground';

function CompareTable({ rows, apptNumber, orderNumber }: { rows: CompareRow[]; apptNumber: string; orderNumber: string }) {
    return (
        <table className="w-full table-fixed border-collapse text-xs">
            <colgroup>
                <col className="w-[26%]" /><col className="w-[34%]" /><col className="w-[6%]" /><col className="w-[34%]" />
            </colgroup>
            <thead>
                <tr className="text-left text-[11px] text-muted-foreground">
                    <th className="pb-1 font-medium" />
                    <th className="pb-1 font-semibold text-foreground">Appointment {apptNumber}</th>
                    <th className="pb-1" />
                    <th className="pb-1 font-semibold text-foreground">WMS order {orderNumber}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.key} className={i === 2 ? 'border-t' : ''}>
                        <td className="py-1 pr-2 align-top text-muted-foreground">{r.label}</td>
                        <td className={`rounded-l px-1.5 py-1 align-top break-words ${cellTone(r.verdict)}`}>{r.appt ?? '—'}</td>
                        <td className={`py-1 align-top ${cellTone(r.verdict)}`}><span className="flex justify-center pt-0.5"><VerdictMark v={r.verdict} /></span></td>
                        <td className={`rounded-r px-1.5 py-1 align-top break-words ${cellTone(r.verdict)}`}>{r.order ?? '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const MIN_SEARCH = 3;

export function WmsOrderMatchDialog({
    open, mode = 'order', orderNumber, orderKey = null, order = null, appointments, total,
    facilityCode = null, getToken, onChoose, onCancel,
}: WmsOrderMatchDialogProps) {
    const listMode = mode === 'noWms';
    const [q, setQ] = useState('');
    const [picked, setPicked] = useState<number | null>(null);
    const [hovered, setHovered] = useState<number | null>(null);
    /* ⭐ The order being compared: one picked from the search, else the one the screen opened for. */
    const [pickedOrder, setPickedOrder] = useState<OrdersPanelRowDto | null>(null);
    const [sq, setSq] = useState('');
    const [results, setResults] = useState<OrdersPanelRowDto[] | null>(null);
    const [searching, setSearching] = useState(false);
    const [searchMsg, setSearchMsg] = useState<string | null>(null);
    /** Which set the results show: a typed search, or one of the day views. */
    const [resultsFrom, setResultsFrom] = useState<'search' | 'unmatched' | 'all' | null>(null);
    /** ⭐ F127: each day's orders on no appointment, read once per day while the screen is open. */
    const [dayOrders, setDayOrders] = useState<Record<string, OrdersPanelRowDto[] | 'loading' | 'error'>>({});

    /* ⛔ Cleared on every open — a selection from the last order must not carry over. */
    useEffect(() => {
        if (open) {
            setQ(''); setPicked(null); setHovered(null);
            setPickedOrder(null); setSq(''); setResults(null); setSearchMsg(null); setResultsFrom(null);
            setDayOrders({});
        }
    }, [open]);

    const list = useMemo(() => {
        /* ⭐ The day list is sorted by time; the No WMS list keeps the server's order (newest first). */
        const all: MatchableAppointment[] = listMode
            ? appointments.filter(a => a.direction !== 'BLOCK' && a.statusCode !== 'CANCELLED')
            : matchCandidates(appointments);
        const needle = q.trim().toLowerCase();
        return needle ? all.filter(a => haystack(a).includes(needle)) : all;
    }, [appointments, q, listMode]);

    /* ⭐ THE FIRST LOAD IS SELECTED — on open, and whenever the filter (or an attach)
       drops the selected one. An empty list selects nothing. */
    useEffect(() => {
        if (!open) return;
        if (list.length === 0) { if (picked !== null) setPicked(null); return; }
        if (picked === null || !list.some(a => a.appointmentId === picked)) setPicked(list[0].appointmentId);
    }, [open, list, picked]);

    /* ⛔ In the No WMS list an order found for ONE load is not a candidate for the next:
       moving to another load clears the picked order (the search results stay). */
    useEffect(() => { if (listMode) setPickedOrder(null); }, [picked, listMode]);

    const chosen = picked === null ? null : list.find(a => a.appointmentId === picked) ?? null;
    const previewed = hovered === null ? null : list.find(a => a.appointmentId === hovered) ?? null;
    /** What the right side shows: the hovered load, else the selection. */
    const shown = previewed ?? chosen;
    const previewing = !!previewed && previewed.appointmentId !== chosen?.appointmentId;

    const canSearch = !!facilityCode && !!getToken;

    /* ⭐ F127: read the shown load's day once (a short wait, so a fast sweep does not
       fire a read per row it crosses). */
    const shownDay = listMode && shown ? dayOf(shown.scheduledStartUtc) : null;
    useEffect(() => {
        if (!open || !listMode || !shownDay || !canSearch || !facilityCode || !getToken) return;
        if (dayOrders[shownDay] !== undefined) return;
        const t = window.setTimeout(() => {
            setDayOrders(m => ({ ...m, [shownDay]: 'loading' }));
            void (async () => {
                try {
                    const r = await getPanelOrders(await getToken(), {
                        facilityCode, from: shownDay, to: shownDay, statusGroups: null,
                    });
                    const free = r.rows.filter(x => (x.bookedOn ?? []).length === 0);
                    setDayOrders(m => ({ ...m, [shownDay]: free }));
                } catch {
                    setDayOrders(m => ({ ...m, [shownDay]: 'error' }));
                }
            })();
        }, 150);
        return () => window.clearTimeout(t);
    }, [open, listMode, shownDay, canSearch, facilityCode, getToken, dayOrders]);

    const dayState = shownDay ? dayOrders[shownDay] : undefined;
    const possible = useMemo<PossibleMatch[]>(
        () => (shown && Array.isArray(dayState) ? rankPossibleMatches(shown, dayState) : []),
        [shown, dayState],
    );
    /** ⭐ The best suggestion is COMPARED straight away, but never attached until clicked. */
    const suggested = listMode && !pickedOrder ? (possible[0]?.row ?? null) : null;

    const activeOrder: OrdersPanelRowDto | null = pickedOrder ?? (listMode ? suggested : order);
    const activeKey: WmsOrderDragKey | null = pickedOrder
        ? wmsOrderKeyOf(pickedOrder)
        : (listMode ? null : (orderKey ?? (order ? wmsOrderKeyOf(order) : null)));
    const activeNumber = activeOrder?.orderNumber ?? (listMode ? '' : orderNumber);

    /* ⛔ The left list's chips score against a CHOSEN order only — never against a
       suggestion that changes with the hover. */
    const scoreOrder = listMode ? pickedOrder : activeOrder;
    const scores = useMemo(() => {
        const m = new Map<number, number>();
        if (scoreOrder) for (const a of list) m.set(a.appointmentId, matchCount(compareApptToOrder(a, scoreOrder)));
        return m;
    }, [list, scoreOrder]);

    const rows = shown && activeOrder ? compareApptToOrder(shown, activeOrder) : null;

    const resultScores = useMemo(() => {
        const m = new Map<string, number>();
        if (shown && results) for (const r of results) m.set(`${r.wmsSource}|${r.orderType}|${r.orderNumber}`, matchCount(compareApptToOrder(shown, r)));
        return m;
    }, [results, shown]);

    /**
     * ⭐ THE DAY VIEWS. The selected load's local date, every status; `unmatched`
     * keeps the rows on no appointment — the Orders panel's own `isUnmatched` test
     * (`bookedOn` empty).
     */
    const runDayView = async (which: 'unmatched' | 'all') => {
        if (!canSearch || !facilityCode || !getToken) return;
        setSearching(true); setSearchMsg(null); setResultsFrom(which);
        try {
            const day = dayOf(chosen?.scheduledStartUtc);
            const r = await getPanelOrders(await getToken(), {
                facilityCode, from: day, to: day, statusGroups: null,
            });
            const rowsIn = which === 'unmatched' ? r.rows.filter(x => (x.bookedOn ?? []).length === 0) : r.rows;
            const sorted = sortOrderResults(rowsIn);
            setResults(sorted);
            const label = which === 'unmatched' ? 'not matched' : 'scheduled';
            setSearchMsg(sorted.length === 0 ? `No orders ${label} for ${day}.`
                : `${sorted.length} order${sorted.length === 1 ? '' : 's'} ${label} for ${day}${r.capped ? ' (first page only — search to narrow)' : ''}.`);
        } catch (e) {
            setResults(null);
            setSearchMsg(`Could not load the orders: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setSearching(false);
        }
    };
    const runSearch = async () => {
        const text = sq.trim();
        if (!canSearch || !facilityCode || !getToken) return;
        if (text.length < MIN_SEARCH) { setSearchMsg(`Type at least ${MIN_SEARCH} characters to search every date.`); return; }
        setSearching(true); setSearchMsg(null); setResultsFrom('search');
        try {
            const day = dayOf(chosen?.scheduledStartUtc);
            const r = await getPanelOrders(await getToken(), {
                facilityCode, from: day, to: day, q: text,
                statusGroups: null,             // every status: a past load's order is usually closed
            });
            const sorted = sortOrderResults(r.rows);
            setResults(sorted);
            setSearchMsg(sorted.length === 0 ? 'No WMS order matches that.'
                : r.capped ? `Showing the first ${sorted.length} — narrow the search to see the rest.` : null);
        } catch (e) {
            setResults(null);
            setSearchMsg(`The search failed: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setSearching(false);
        }
    };

    /** One order row — the possible matches and the search results draw the same row. */
    const orderRow = (r: OrdersPanelRowDto, n: number | undefined) => {
        const k = `${r.wmsSource}|${r.orderType}|${r.orderNumber}`;
        const on = !!pickedOrder && pickedOrder.orderNumber === r.orderNumber
            && pickedOrder.orderType === r.orderType && pickedOrder.wmsSource === r.wmsSource;
        const attachable = wmsOrderKeyOf(r) !== null;
        return (
            <button
                key={k}
                type="button"
                disabled={!attachable}
                aria-pressed={on}
                onClick={() => setPickedOrder(r)}
                title={attachable ? 'Compare this order with the load' : 'This order is missing part of its key and cannot be attached'}
                className={'flex w-full items-center gap-2 rounded border px-2 py-1 text-left text-xs disabled:opacity-50 '
                    + (on ? 'border-primary bg-primary/10' : 'hover:bg-muted/60')}
            >
                <span className="shrink-0 tabular-nums text-muted-foreground">{r.effectiveDate?.slice(0, 10) ?? '—'}</span>
                <span className="font-medium">{r.orderNumber}</span>
                <span className="shrink-0 text-muted-foreground">{r.direction === 'OUTBOUND' ? 'Out' : r.direction === 'INBOUND' ? 'In' : ''}</span>
                <span className="min-w-0 truncate text-muted-foreground">
                    {[r.businessUnit, r.customer, r.shipVia, r.status].filter(Boolean).join(' · ')}
                </span>
                {r.bookedCount > 0 && (
                    <span className="shrink-0 rounded bg-muted px-1 text-[10px]" title="Already on an appointment">booked {r.bookedCount}</span>
                )}
                {n !== undefined && n > 0 && (
                    <span className="ml-auto shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-800"
                          title={`${n} of 8 rows agree with the selected load`}>✓ {n}</span>
                )}
            </button>
        );
    };

    const title = listMode
        ? `No WMS — ${total ?? appointments.length} load${(total ?? appointments.length) === 1 ? '' : 's'} with no WMS order`
        : `Match ${orderNumber} to an appointment`;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {listMode
                            ? 'Pick a load, search the WMS for its order (client, customer, PO, reference…), click the order to compare, then Attach. The next load is selected when one is matched.'
                            : 'Hover a load to compare it with the order; click to choose it. Nothing is attached until you press Attach. If the order is already on another load you will be asked what to do.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid min-h-0 grid-cols-1 gap-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
                    {/* ---- LEFT: the loads ---- */}
                    <div className="flex min-h-0 flex-col gap-2">
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Filter by appointment, door, client, carrier, PO…"
                            autoFocus
                        />
                        <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1"
                             onMouseLeave={() => setHovered(null)}>
                            {list.length === 0 && (
                                <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                                    {q.trim() ? 'No appointment matches that.'
                                        : listMode ? 'Every load on the list has a WMS order now.' : 'There are no appointments today an order can go on.'}
                                </p>
                            )}
                            {list.map(a => {
                                const time = appointmentTimeLabel(a, hhmm(a.scheduledStartUtc));
                                const when = listMode
                                    ? `${new Date(a.scheduledStartUtc).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`
                                    : time;
                                const on = a.appointmentId === picked;
                                const hov = a.appointmentId === hovered && !on;
                                const n = scores.get(a.appointmentId);
                                return (
                                    <button
                                        key={a.appointmentId}
                                        type="button"
                                        aria-pressed={on}
                                        onMouseEnter={() => setHovered(a.appointmentId)}
                                        onFocus={() => setHovered(a.appointmentId)}
                                        onClick={() => setPicked(a.appointmentId)}
                                        className={'flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left text-sm '
                                            + (on ? 'border-primary bg-primary/10' : hov ? 'border-primary/40 bg-muted/60' : '')}
                                    >
                                        <span className="font-medium">{a.appointmentNumber}</span>
                                        <span className="shrink-0 text-muted-foreground">
                                            {[a.dockDoorCode, when].filter(Boolean).join(' · ')}
                                        </span>
                                        <span className="ml-auto truncate text-xs text-muted-foreground">
                                            {[a.clientName || a.clientCode, a.carrierName || a.carrierCode].filter(Boolean).join(' · ')}
                                        </span>
                                        {n !== undefined && n > 0 && (
                                            <span className="shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-800"
                                                  title={`${n} of 8 rows agree with the order`}>
                                                ✓ {n}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            {listMode && total !== undefined && total > appointments.length && (
                                <p className="px-2 py-1 text-[11px] text-muted-foreground">
                                    Showing the {appointments.length} most recent of {total}; older ones appear as these are matched.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ---- RIGHT: the comparison, then the order search ---- */}
                    <div className="flex min-h-0 max-h-[70vh] flex-col gap-3 overflow-y-auto rounded border p-3">
                        {!activeOrder ? (
                            <p className="text-sm text-muted-foreground">
                                {listMode
                                    ? (dayState === 'loading' || (shownDay && dayState === undefined)
                                        ? 'Looking for possible matches on this load’s day…'
                                        : 'No possible match on this load’s day. Search the WMS below and click an order to compare it with this load.')
                                    : 'The order’s details did not come with this request, so there is nothing to compare. Pick a load and Attach, or search for the order below.'}
                            </p>
                        ) : !rows || !shown ? (
                            <p className="text-sm text-muted-foreground">
                                {q.trim() ? 'No load matches the filter.' : 'There are no loads to compare.'}
                            </p>
                        ) : (
                            <div>
                                {previewing && chosen && (
                                    <p className="mb-2 rounded bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                                        Previewing {shown.appointmentNumber}. Click it to choose it; Attach still goes
                                        to <span className="font-medium text-foreground">{chosen.appointmentNumber}</span>.
                                    </p>
                                )}
                                {suggested && (
                                    <p className="mb-2 rounded bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900">
                                        Best possible match for {shown.appointmentNumber}. Click it in the list below to choose it, then Attach.
                                    </p>
                                )}
                                <CompareTable rows={rows} apptNumber={shown.appointmentNumber} orderNumber={activeNumber} />
                            </div>
                        )}
                        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> agrees</span>
                            <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" /> differs</span>
                            <span className="inline-flex items-center gap-1"><Minus className="h-3 w-3" /> one side blank</span>
                            <span>Case, spaces and dashes are ignored.</span>
                        </p>

                        {/* ---- ⭐ F127: possible matches for the load on the left ---- */}
                        {listMode && canSearch && shown && (
                            <div className="flex flex-col gap-1.5 border-t pt-2" data-possible-matches="">
                                <p className="text-[11px] font-semibold text-foreground">
                                    Possible matches for {shown.appointmentNumber}
                                    <span className="ml-1 font-normal text-muted-foreground">
                                        · {shownDay}, orders on no appointment
                                    </span>
                                </p>
                                {dayState === 'error' && (
                                    <p className="text-[11px] text-muted-foreground">Could not read this day’s orders — search below instead.</p>
                                )}
                                {Array.isArray(dayState) && possible.length === 0 && (
                                    <p className="text-[11px] text-muted-foreground">None share a PO, reference, container, BOL or the client and direction.</p>
                                )}
                                {possible.length > 0 && (
                                    <div className="space-y-1">
                                        {possible.map(p => orderRow(p.row, p.agree))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ---- ⭐ F107: the WMS order search, below the legend ---- */}
                        {canSearch && (
                            <div className="flex flex-col gap-1.5 border-t pt-2" data-wms-order-search="">
                                <form className="flex items-center gap-2"
                                      onSubmit={(e) => { e.preventDefault(); void runSearch(); }}>
                                    <Input
                                        value={sq}
                                        onChange={(e) => setSq(e.target.value)}
                                        placeholder="Search WMS orders — client, customer, PO, reference, container, BOL…"
                                    />
                                    <Button type="submit" size="sm" variant="outline" disabled={searching}>
                                        <Search className="mr-1 h-3.5 w-3.5" />{searching ? 'Searching…' : 'Search'}
                                    </Button>
                                    {/* ⭐ The Orders panel's two views, for the selected load's day. */}
                                    <Button type="button" size="sm" variant={resultsFrom === 'unmatched' ? 'default' : 'outline'}
                                            disabled={searching} onClick={() => void runDayView('unmatched')}
                                            title="Not matched — orders the WMS dates for this load's day that are on no appointment"
                                            aria-label="Not matched">
                                        <Unlink className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button type="button" size="sm" variant={resultsFrom === 'all' ? 'default' : 'outline'}
                                            disabled={searching} onClick={() => void runDayView('all')}
                                            title="All scheduled — every order the WMS dates for this load's day"
                                            aria-label="All scheduled">
                                        <CalendarDays className="h-3.5 w-3.5" />
                                    </Button>
                                </form>
                                {searchMsg && <p className="text-[11px] text-muted-foreground">{searchMsg}</p>}
                                {results && results.length > 0 && (
                                    <div className="max-h-[32vh] space-y-1 overflow-y-auto pr-1">
                                        {results.map(r => orderRow(r, resultScores.get(`${r.wmsSource}|${r.orderType}|${r.orderNumber}`)))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" size="sm" onClick={onCancel}>{listMode ? 'Close' : 'Cancel'}</Button>
                    <Button size="sm" disabled={!chosen || !activeKey}
                            onClick={() => { if (chosen && activeKey) onChoose(chosen, activeKey, activeOrder); }}>
                        {chosen && activeKey ? `Attach ${activeKey.orderNumber} to ${chosen.appointmentNumber}` : 'Attach'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
